import React, { Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { ProtectedRoute } from "@/components/Auth/ProtectedRoute";
import {
  DashboardLayout,
  AppLayout,
  AdminLayout,
  TenantSettingsLayout,
} from "@/components/Layouts";
import { LoadingSpinner } from "@/components/shared/LoadingSpinner";

// Lazy load pages for better performance
const Dashboard = React.lazy(() =>
  import("@/pages/Dashboard/Dashboard").then((module) => ({ default: module.Dashboard }))
);
const FleetDetailPage = React.lazy(() =>
  import("@/pages/fleet/detail").then((module) => ({
    default: module.FleetDetailPage,
  }))
);
const FleetListPage = React.lazy(() =>
  import("@/pages/fleet/list/FleetListPage").then((module) => ({
    default: module.FleetListPage,
  }))
);
const Unit = React.lazy(() =>
  import("@/pages/Unit").then((module) => ({ default: module.Unit }))
);
const UnitDetails = React.lazy(() =>
  import("@/pages/Unit/Details").then((module) => ({
    default: module.UnitDetails,
  }))
);
const UnitGraph = React.lazy(() =>
  import("@/pages/Unit/Graph").then((module) => ({ default: module.UnitGraph }))
);
const UnitExport = React.lazy(() =>
  import("@/pages/Unit/Export").then((module) => ({
    default: module.UnitExport,
  }))
);
const UnitDiagnostics = React.lazy(() =>
  import("@/pages/Unit/Diagnostics").then((module) => ({
    default: module.UnitDiagnostics,
  }))
);
const WagonDetails = React.lazy(() =>
  import("@/pages/Unit/Wagon").then((module) => ({
    default: module.WagonDetails,
  }))
);
const BogieDetails = React.lazy(() =>
  import("@/pages/Unit/Bogie").then((module) => ({
    default: module.BogieDetails,
  }))
);
const WheelDetails = React.lazy(() =>
  import("@/pages/Unit/Wagon/Bogie/Wheel").then((module) => ({
    default: module.WheelDetails,
  }))
);
const GearboxSystem = React.lazy(() =>
  import("@/pages/Unit/Wagon/GearboxSystem").then((module) => ({
    default: module.GearboxSystem,
  }))
);
const WheelSystem = React.lazy(() =>
  import("@/pages/Unit/Wagon/WheelSystem").then((module) => ({
    default: module.WheelSystem,
  }))
);
const ComponentDetails = React.lazy(() =>
  import("@/pages/Unit/Component").then((module) => ({
    default: module.ComponentDetails,
  }))
);
const Login = React.lazy(() =>
  import("@/pages/Login/Login").then((module) => ({ default: module.Login }))
);
const Profile = React.lazy(() =>
  import("@/pages/profile/index").then((module) => ({
    default: module.ProfilePage,
  }))
);
const AlarmDetail = React.lazy(() =>
  import("@/pages/AlarmDetail").then((module) => ({
    default: module.AlarmDetail,
  }))
);
const Notifications = React.lazy(() =>
  import("@/pages/Notifications").then((module) => ({
    default: module.Notifications,
  }))
);

// Admin pages
const UserListPage = React.lazy(() =>
  import("@/pages/admin").then((module) => ({ default: module.UserListPage }))
);
const RoleListPage = React.lazy(() =>
  import("@/pages/admin").then((module) => ({ default: module.RoleListPage }))
);
const PermissionListPage = React.lazy(() =>
  import("@/pages/admin").then((module) => ({
    default: module.PermissionListPage,
  }))
);

const TenantListPage = React.lazy(() =>
  import("@/pages/admin").then((module) => ({ default: module.TenantListPage }))
);
const TenantDetailPage = React.lazy(() =>
  import("@/pages/admin").then((module) => ({
    default: module.TenantDetailPage,
  }))
);
const AdminUnitDetailPage = React.lazy(() =>
  import("@/pages/admin/units").then((module) => ({
    default: module.UnitDetailPage,
  }))
);

const TenantCompanyPage = React.lazy(() =>
  import("@/pages/settings").then((module) => ({
    default: module.CompanyPage,
  }))
);
const TenantUsersPage = React.lazy(() =>
  import("@/pages/settings").then((module) => ({
    default: module.UsersPage,
  }))
);
const TenantRolesPage = React.lazy(() =>
  import("@/pages/settings").then((module) => ({
    default: module.RolesPage,
  }))
);
const TenantPermissionsPage = React.lazy(() =>
  import("@/pages/settings").then((module) => ({
    default: module.PermissionsPage,
  }))
);
const MonitoringPage = React.lazy(() =>
  import("@/pages/admin").then((module) => ({ default: module.MonitoringPage }))
);
const ProceduresPage = React.lazy(() =>
  import("@/pages/admin").then((module) => ({ default: module.ProceduresPage }))
);
const AssetsPage = React.lazy(() =>
  import("@/pages/admin").then((module) => ({ default: module.AssetsPage }))
);
const ReliabilityPage = React.lazy(() =>
  import("@/pages/admin").then((module) => ({
    default: module.ReliabilityPage,
  }))
);
const DownloadAppPage = React.lazy(() =>
  import("@/pages/admin").then((module) => ({
    default: module.DownloadAppPage,
  }))
);
const ControlPanelPage = React.lazy(() =>
  import("@/pages/admin").then((module) => ({
    default: module.ControlPanelPage,
  }))
);
const AdminDashboard = React.lazy(() =>
  import("@/pages/admin").then((module) => ({
    default: module.AdminDashboard,
  }))
);

/**
 * Application Router Configuration
 *
 * This file contains all route definitions following SOLID principles:
 * - Single Responsibility: Each route has a single purpose
 * - Open/Closed: Easy to extend with new routes
 * - Dependency Inversion: Uses dependency injection for layouts
 */
export const AppRouter: React.FC = () => {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<Login />} />

        {/* Protected Dashboard Routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Dashboard />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* Fleet Management Routes */}
        <Route
          path="/fleets"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <FleetListPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/fleets/:id"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <FleetDetailPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* Unit Management Routes */}
        <Route
          path="/units"
          element={
            <ProtectedRoute>
              <AppLayout>
                <Unit />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/units/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <UnitDetails />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/units/:id/graph"
          element={
            <ProtectedRoute>
              <AppLayout>
                <UnitGraph />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/units/:id/export"
          element={
            <ProtectedRoute>
              <AppLayout>
                <UnitExport />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/units/:id/diagnostics"
          element={
            <ProtectedRoute>
              <AppLayout>
                <UnitDiagnostics />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/units/:unitId/components/:componentId"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ComponentDetails />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Wagon Management Routes */}
        <Route
          path="/wagons/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <WagonDetails />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Bogie Management Routes */}
        <Route
          path="/bogies/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <BogieDetails />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Wheel Management Routes */}
        <Route
          path="/bogies/:bogieId/wheels"
          element={
            <ProtectedRoute>
              <AppLayout>
                <WheelSystem />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/bogies/:bogieId/wheels/:wheelId"
          element={
            <ProtectedRoute>
              <AppLayout>
                <WheelDetails />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* Gearbox Management Routes */}
        <Route
          path="/bogies/:bogieId/gearboxes/:id"
          element={
            <ProtectedRoute>
              <AppLayout>
                <GearboxSystem />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        {/* User Management Routes */}
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Profile />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* Alert and Notification Routes */}
        <Route
          path="/alarm-detail"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <AlarmDetail />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Notifications />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />

        {/* Admin Routes */}
        <Route
          path="/admin/profile"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <Profile />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <UserListPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/roles"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <RoleListPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/permissions"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <PermissionListPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/tenants"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <TenantListPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/tenants/:id"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <TenantDetailPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/tenants/:tenantId/units/:unitId"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <AdminUnitDetailPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/monitoring"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <MonitoringPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/procedures"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <ProceduresPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/assets"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <AssetsPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/reliability"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <ReliabilityPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/download-app"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <DownloadAppPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/control-panel"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <ControlPanelPage />
              </AdminLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminLayout>
                <AdminDashboard />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        {/* Tenant Settings Routes */}
        <Route
          path="/settings/profile"
          element={
            <ProtectedRoute>
              <TenantSettingsLayout>
                <Profile />
              </TenantSettingsLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/company"
          element={
            <ProtectedRoute>
              <TenantSettingsLayout>
                <TenantCompanyPage />
              </TenantSettingsLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/users"
          element={
            <ProtectedRoute>
              <TenantSettingsLayout>
                <TenantUsersPage />
              </TenantSettingsLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/roles"
          element={
            <ProtectedRoute>
              <TenantSettingsLayout>
                <TenantRolesPage />
              </TenantSettingsLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/permissions"
          element={
            <ProtectedRoute>
              <TenantSettingsLayout>
                <TenantPermissionsPage />
              </TenantSettingsLayout>
            </ProtectedRoute>
          }
        />

        {/* Default Route */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

        {/* 404 Route */}
        <Route
          path="*"
          element={
            <div className="min-h-screen flex items-center justify-center bg-ds-dark">
              <div className="text-center">
                <h1 className="text-4xl font-bold text-white mb-4">404</h1>
                <p className="text-ds-text mb-8">Page not found</p>
                <button
                  onClick={() => window.history.back()}
                  className="px-6 py-2 bg-ds-accent text-ds-dark font-semibold rounded hover:bg-ds-accent/80 transition-colors"
                >
                  Go Back
                </button>
              </div>
            </div>
          }
        />
      </Routes>
    </Suspense>
  );
};
